/**
 * Document tiles — the shared card / row renderer for document lists.
 *
 * Lifted out of isg-intranet's documents.js on 2026-07-31, when five more
 * apps needed the same thing. Their admin document surfaces listed plain
 * text rows: no thumbnail, and no way to open a document at all, so the
 * people who UPLOAD the documents were the only ones who couldn't see
 * them. Six copies of this was the alternative.
 *
 * Pairs with `doc-viewer.js` — build the tiles here, open them there.
 */
import { formatFileSize } from '../utils/formatters.js';
import { t } from '../i18n/index.js';

const FILE_ICONS = {
  'application/pdf': { icon: 'PDF', color: 'var(--red-600)', bg: 'var(--red-50)' },
  'application/msword': { icon: 'DOC', color: 'var(--blue-600)', bg: 'var(--blue-50)' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: 'DOCX', color: 'var(--blue-600)', bg: 'var(--blue-50)' },
  'application/vnd.ms-excel': { icon: 'XLS', color: 'var(--green-600)', bg: 'var(--green-50)' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: 'XLSX', color: 'var(--green-600)', bg: 'var(--green-50)' },
  'application/vnd.ms-powerpoint': { icon: 'PPT', color: 'var(--amber-600)', bg: 'var(--amber-50)' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: 'PPTX', color: 'var(--amber-600)', bg: 'var(--amber-50)' },
  'text/plain': { icon: 'TXT', color: 'var(--gray-600)', bg: 'var(--gray-100)' },
  'text/csv': { icon: 'CSV', color: 'var(--green-600)', bg: 'var(--green-50)' },
  'application/zip': { icon: 'ZIP', color: 'var(--purple-600)', bg: 'var(--purple-50)' },
  'image/png': { icon: 'PNG', color: 'var(--pink-600)', bg: 'var(--pink-50)' },
  'image/jpeg': { icon: 'JPG', color: 'var(--pink-600)', bg: 'var(--pink-50)' },
  'image/gif': { icon: 'GIF', color: 'var(--pink-600)', bg: 'var(--pink-50)' },
  'image/svg+xml': { icon: 'SVG', color: 'var(--purple-600)', bg: 'var(--purple-50)' },
};

const EXT_FALLBACK = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
  zip: 'application/zip',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  svg: 'image/svg+xml',
};

const DEFAULT_FILE_ICON = { icon: 'FILE', color: 'var(--gray-600)', bg: 'var(--gray-100)' };

/**
 * File-type chip for a document.
 * @param {string} [mimeType]
 * @param {string} [filename] — used when mime_type is missing or generic
 *   (several apps store application/octet-stream for everything)
 */
export function getFileIcon(mimeType, filename) {
  const direct = FILE_ICONS[mimeType];
  if (direct) return direct;
  const ext = String(filename || '').split('?')[0].split('.').pop().toLowerCase();
  return FILE_ICONS[EXT_FALLBACK[ext]] || DEFAULT_FILE_ICON;
}

/**
 * Thumbnail <img> over an icon-tile fallback.
 *
 * The img starts transparent and fades in on load; on error (404 = this
 * file has no renderable preview) it removes itself, leaving the tile. So
 * a missing thumbnail degrades to a labelled chip rather than a broken
 * image, and no request has to be made twice to find that out.
 *
 * Pass thumbUrl = null to render the tile alone.
 */
export function docThumbTile(thumbUrl, fi, w, h, radius = 'var(--radius-md)', labelSize = '0.65rem') {
  return `
    <div style="position:relative; width:${w}; height:${h}; border-radius:${radius}; background:${fi.bg};
      display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden;">
      <span style="font-weight:700; font-size:${labelSize}; color:${fi.color};">${fi.icon}</span>
      ${thumbUrl ? `<img class="doc-thumb" src="${thumbUrl}" alt="" loading="lazy"
        style="position:absolute; inset:0; width:100%; height:100%; object-fit:cover; object-position:top; opacity:0; transition:opacity 0.15s;" />` : ''}
    </div>`;
}

/**
 * Wire fade-in / self-removal for every thumbnail under `rootEl`.
 * MUST be called after the markup is inserted — CSP blocks inline
 * onload/onerror handlers estate-wide.
 */
export function wireDocThumbs(rootEl) {
  if (!rootEl) return;
  rootEl.querySelectorAll('img.doc-thumb').forEach((img) => {
    img.addEventListener('load', () => { img.style.opacity = '1'; });
    img.addEventListener('error', () => img.remove());
  });
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Card/list toggle markup. `mode` is 'grid' | 'list'.
 * Buttons carry data-set-view; wire with wireDocViewToggle().
 */
export function docViewToggle(mode) {
  const btn = (m, title, svg) => `
    <button type="button" data-set-view="${m}" title="${title}" aria-label="${title}"
      style="padding:6px 10px; border:1px solid ${mode === m ? 'var(--brand-300)' : 'var(--gray-200)'};
      border-radius:var(--radius-md); background:${mode === m ? 'var(--brand-50)' : 'var(--surface-primary)'}; cursor:pointer;">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${mode === m ? 'var(--brand-600)' : 'var(--gray-500)'}" stroke-width="2">${svg}</svg>
    </button>`;
  return `<div style="display:flex; gap:var(--space-1);">
    ${btn('grid', t('docs.view_cards'), '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>')}
    ${btn('list', t('docs.view_list'), '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>')}
  </div>`;
}

export function wireDocViewToggle(rootEl, onChange) {
  if (!rootEl) return;
  rootEl.querySelectorAll('[data-set-view]').forEach((b) =>
    b.addEventListener('click', () => onChange(b.dataset.setView)));
}

/**
 * Render a list of documents as cards or rows.
 *
 * @param {Array}  docs
 * @param {Object} opts
 * @param {'grid'|'list'} [opts.mode='grid']
 * @param {Function} [opts.thumbUrl]  — (doc) => url | null
 * @param {Function} [opts.title]     — (doc) => string      (default: doc.title || doc.file_name)
 * @param {Function} [opts.subtitle]  — (doc) => string      (shown under the title)
 * @param {Function} [opts.meta]      — (doc) => string HTML (footer line; defaults to size + type chip)
 * @param {Function} [opts.actions]   — (doc) => string HTML (admin buttons; keep them [data-*]-driven
 *                                       and call stopPropagation, or they also open the preview)
 * @param {Function} [opts.attrs]     — (doc) => string of extra HTML attributes for the card element
 * @param {boolean}  [opts.dimmed]    — (doc) => truthy to render at reduced opacity (inactive docs)
 *
 * The card element carries data-doc-id; wire clicks yourself so each app
 * keeps control of what a click means.
 */
export function renderDocList(docs, opts = {}) {
  const {
    mode = 'grid', thumbUrl = () => null, title, subtitle, meta, actions,
    attrs = () => '', dimmed = () => false,
  } = opts;
  if (!docs || !docs.length) return '';

  const titleOf = title || ((d) => d.title || d.file_name || d.filename || t('viewer.document'));
  const subOf = subtitle || ((d) => d.description || d.file_name || d.filename || '');
  const metaOf = meta || ((d) => {
    const fi = getFileIcon(d.mime_type, d.file_name || d.filename);
    return `<span>${formatFileSize(d.file_size)}</span>
      <span style="font-size:0.7rem; font-weight:600; color:${fi.color}; background:${fi.bg}; padding:2px 8px; border-radius:var(--radius-full);">${fi.icon}</span>`;
  });

  const card = (d) => {
    const fi = getFileIcon(d.mime_type, d.file_name || d.filename);
    return `
      <div class="card card-hover doc-tile" data-doc-id="${esc(d.id)}" ${attrs(d)}
        style="cursor:pointer; transition:all 0.15s; display:flex; flex-direction:column; padding:0; overflow:hidden;${dimmed(d) ? 'opacity:0.6;' : ''}">
        ${docThumbTile(thumbUrl(d), fi, '100%', '180px', '0', '1.05rem')}
        <div style="display:flex; flex-direction:column; gap:var(--space-2); padding:var(--space-3); flex:1;">
          <div style="min-width:0;">
            <div style="font-weight:600; font-size:0.9rem; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(titleOf(d))}</div>
            <div style="font-size:0.75rem; color:var(--text-tertiary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(subOf(d))}</div>
          </div>
          <div style="display:flex; align-items:center; gap:var(--space-3); font-size:0.75rem; color:var(--text-secondary); margin-top:auto; padding-top:var(--space-2); border-top:1px solid var(--border-secondary);">
            ${metaOf(d)}
          </div>
          ${actions ? `<div class="doc-tile-actions" style="display:flex; gap:var(--space-2); flex-wrap:wrap;">${actions(d)}</div>` : ''}
        </div>
      </div>`;
  };

  const row = (d) => {
    const fi = getFileIcon(d.mime_type, d.file_name || d.filename);
    return `
      <div class="card-hover doc-tile" data-doc-id="${esc(d.id)}" ${attrs(d)}
        style="display:flex; align-items:center; gap:var(--space-3); padding:var(--space-3);
        border:1px solid var(--border-secondary); border-radius:var(--radius-md); cursor:pointer;
        background:var(--surface-primary); transition:all 0.15s;${dimmed(d) ? 'opacity:0.6;' : ''}">
        ${docThumbTile(thumbUrl(d), fi, '36px', '36px')}
        <div style="flex:1; min-width:0;">
          <div style="font-weight:500; font-size:0.9rem; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(titleOf(d))}</div>
          <div style="font-size:0.75rem; color:var(--text-tertiary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(subOf(d))}</div>
        </div>
        <div style="display:flex; align-items:center; gap:var(--space-3); font-size:0.8rem; color:var(--text-secondary); flex-shrink:0;">
          ${metaOf(d)}
        </div>
        ${actions ? `<div class="doc-tile-actions" style="display:flex; gap:var(--space-2); flex-shrink:0;">${actions(d)}</div>` : ''}
      </div>`;
  };

  return mode === 'grid'
    ? `<div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(240px, 1fr)); gap:var(--space-4);">${docs.map(card).join('')}</div>`
    : `<div style="display:flex; flex-direction:column; gap:var(--space-2);">${docs.map(row).join('')}</div>`;
}

/**
 * Wire "click a tile → open the preview", ignoring clicks on admin
 * buttons inside it. Without the [data-*] escape an Edit click would open
 * the viewer as well as the edit modal.
 */
export function wireDocTiles(rootEl, onOpen) {
  if (!rootEl) return;
  rootEl.querySelectorAll('.doc-tile').forEach((el) => {
    el.addEventListener('click', (e) => {
      if (e.target.closest('button, a, input, select, .doc-tile-actions')) return;
      onOpen(el.dataset.docId, el);
    });
  });
  wireDocThumbs(rootEl);
}

/** Remember the card/list choice per app+surface. */
export function readDocViewMode(key, fallback = 'grid') {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (_) {
    return fallback;
  }
}

export function writeDocViewMode(key, mode) {
  try {
    localStorage.setItem(key, mode);
  } catch (_) { /* private browsing — the toggle still works in-session */ }
}
